import api from "./api";

export interface BookingApplicant {
  id?: number;
  firstName: string;
  fatherHusbandName: string;
  surname: string;
  panCard?: string;
  aadharCard?: string;
  contactNumber: string;
  isPrimary: boolean;
}

export interface BookingPaymentPlan {
  id?: number;
  planType: string;
  amount: number;
  startDate?: string;
  endDate?: string;
  frequency?: string;
  duration?: number;
  description?: string;
  isActive?: boolean;
  // Loan specific fields
  bank?: string;
  byBuilder?: boolean;
  agentName?: string;
  status?: string;
  loanDate?: string;
}

export interface BookingPayment {
  id: number;
  amount: number;
  paymentDate: string;
  paymentMode: string;
  referenceNumber?: string;
  remarks?: string;
  status: string;
  paymentPlan?: BookingPaymentPlan;
}

export interface Unit {
  id: number;
  unitType: string;
  unitNo: string;
  wing: string;
  size: string;
  currentStatus?: string;
  saleDeedAmount?: number;
  extraWorkAmount?: number;
  terraceStatus?: string;
  terraceSize?: string;
  extraSpace?: string;
  extraSpaceAmount?: number;
  project: {
    id: number;
    name: string;
  };
}

export interface Booking {
  id: number;
  bookingId: string;
  brokerId?: number;
  broker?: {
    id: number;
    name: string;
    mobile: string;
  };
  projectId: number;
  project?: {
    id: number;
    name: string;
  };
  unitId: number;
  unit: Unit;
  bookingDate: string;
  customerAddress?: string;
  remarks?: string;
  source: string;
  paymentTerms?: string;
  saleDeedAmount: number;
  chargesAmount: number;
  otherWorkAmount: number;
  terraceAmount: number;
  banakhatAmount: number;
  totalAmount: number;
  status: string;
  authorizedBy?: number;
  authorizedAt?: string;
  applicants: BookingApplicant[];
  paymentPlans: BookingPaymentPlan[];
  payments?: BookingPayment[];
  _count?: {
    payments: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BookingStats {
  totalBookings: number;
  activeBookings: number;
  cancelledBookings: number;
  completedBookings: number;
  totalRevenue: number;
}

export interface CreateBookingData {
  brokerId?: number;
  projectId: number;
  unitId: number;
  bookingDate: string;
  customerAddress?: string;
  remarks?: string;
  source: string;
  planType?: string; // Frontend field for form state
  paymentTerms?: string; // Backend field that gets saved to database
  saleDeedAmount: number;
  chargesAmount?: number;
  otherWorkAmount?: number;
  terraceAmount?: number;
  banakhatAmount?: number;
  applicants: Omit<BookingApplicant, "id">[];
  paymentPlans?: Omit<BookingPaymentPlan, "id" | "isActive">[];
}

export interface UpdateBookingData {
  brokerId?: number;
  customerAddress?: string;
  remarks?: string;
  source?: string;
  paymentTerms?: string;
  saleDeedAmount?: number;
  chargesAmount?: number;
  otherWorkAmount?: number;
  terraceAmount?: number;
  banakhatAmount?: number;
  applicants?: Omit<BookingApplicant, "id">[];
  status?: string;
}

const bookingService = {
  // Get all bookings with pagination and filters
  getBookings: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    projectId?: number;
    status?: string;
    wing?: string;
    unitType?: string;
    unitSize?: string;
    memberName?: string;
    mobile?: string;
    unitNo?: string;
    bookingDateFrom?: string;
    bookingDateTo?: string;
    cancelledDateFrom?: string;
    cancelledDateTo?: string;
  }) => {
    const response = await api.get("/bookings", { params });
    return {
      bookings: response.data.bookings as Booking[],
      pagination: response.data.pagination,
    };
  },

  // Get single booking by ID
  getBookingById: async (id: number): Promise<Booking> => {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  },

  // Get unsold units by project
  getUnsoldUnits: async (projectId: number): Promise<Unit[]> => {
    const response = await api.get(`/bookings/units/${projectId}`);
    return response.data.units;
  },

  // Create new booking
  createBooking: async (data: CreateBookingData): Promise<Booking> => {
    const response = await api.post("/bookings", data);
    return response.data;
  },

  // Update booking
  updateBooking: async (
    id: number,
    data: UpdateBookingData
  ): Promise<Booking> => {
    const response = await api.put(`/bookings/${id}`, data);
    return response.data;
  },

  // Cancel booking with optional refund
  cancelBooking: async (
    id: number,
    cancellationData: {
      reason: string;
      refundAmount?: number;
      refundDetails?: {
        refundDate: string;
        paymentMethod: string;
        referenceNumber: string;
        remarks?: string;
      };
    }
  ) => {
    const response = await api.put(`/bookings/${id}/cancel`, cancellationData);
    return response.data;
  },

  // Transfer booking
  transferBooking: async (
    id: number,
    transferData: {
      transferType: "name" | "unit";
      newUnitId?: number;
      newApplicants?: Omit<BookingApplicant, "id">[];
      newAmounts?: {
        saleDeedAmount: number;
        chargesAmount: number;
        otherWorkAmount: number;
        terraceAmount: number;
        banakhatAmount: number;
      };
      reason: string;
      password: string;
    }
  ): Promise<Booking> => {
    const response = await api.put(`/bookings/${id}/transfer`, transferData);
    return response.data;
  },

  // Delete booking
  deleteBooking: async (id: number, password: string): Promise<void> => {
    await api.delete(`/bookings/${id}`, { data: { password } });
  },

  // Get booking history by unit
  getBookingHistoryByUnit: async (unitId: number) => {
    const response = await api.get(`/bookings/history/unit/${unitId}`);
    return response.data;
  },

  // Get booking statistics
  getBookingStats: async (projectId?: number): Promise<BookingStats> => {
    const params = projectId ? { projectId } : {};
    const response = await api.get("/bookings/stats", { params });
    return response.data;
  },

  // Get pending bookings for approval
  getPendingBookings: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    projectId?: string;
  }) => {
    const response = await api.get("/bookings/pending", { params });
    return {
      bookings: response.data.bookings as Booking[],
      pagination: response.data.pagination,
    };
  },

  // Authorize (approve/reject) bookings
  authorizeBookings: async (data: {
    bookingIds: number[];
    action: "approve" | "reject";
    reason?: string;
  }) => {
    const response = await api.post("/bookings/authorize", data);
    return response.data;
  },

  // Get booking edit history from Receivables
  getBookingEditHistory: async (bookingId: number) => {
    const response = await api.get(`/bookings/${bookingId}/edit-history`);
    return response.data;
  },

  // Get transferred bookings
  getTransferredBookings: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    projectId?: string;
  }) => {
    const response = await api.get("/bookings/transferred", { params });
    return {
      transfers: response.data.data,
      pagination: response.data.pagination,
    };
  },
};

export default bookingService;
